import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import toast from "react-hot-toast";

import checkoutCore, { CheckoutSuccessRes, SessionItem, setRecoveryCartItems } from "../../core/checkout.core";
import { logClientError } from "../../core/log.core";
import useCheckoutSession from "../../components/common/useCheckoutSession.hook";
import { useTranslation } from "../../core/translation.core";
import { checkoutSessionQueryKeys, syncCheckoutSessionCachesIfSameId } from "../cart/checkoutSessionCache";
import { CartFormData, CartItem, DeliveryOption } from "../../types/cart";
import { CheckoutSession } from "../../types/checkout";

interface UseSelectOptionProps {
  deliveryOptions: DeliveryOption[] | undefined;
  methods: UseFormReturn<CartFormData>;
  items: CartItem[];
  isTemp?: boolean;
}

// Keep the module-level recovery items aligned with the live cart so a session can always be
// recreated in the background, even when the checkout-session provider state is stale
function cartItemsToSessionItems(items: CartItem[]): SessionItem[] {
  return items.map((i) => {
    const attachToIdx = i.attachCartItemId ? items.findIndex((ci) => ci.id === i.attachCartItemId) : -1;
    return {
      productId: i.product.id,
      quantity: i.quantity,
      discountPrice: i.discountPrice,
      addOnDiscountId: i.addOnDiscount?.id,
      attachToIdx: attachToIdx >= 0 ? attachToIdx : undefined,
      isPointRedeemed: i.isPointRedeemed,
    };
  });
}

export default function useSelectShipOption({ deliveryOptions, items, isTemp = false }: UseSelectOptionProps) {
  const queryClient = useQueryClient();
  const { t, msg } = useTranslation();
  const { data: checkoutSession } = useCheckoutSession(isTemp);

  const checkoutSessionKey = isTemp ? checkoutSessionQueryKeys.temp : checkoutSessionQueryKeys.checkout;

  const isSessionReady = !!checkoutSession;

  useEffect(() => {
    // never clear recovery items from here; only refresh them while the cart has items
    if (items.length > 0) {
      setRecoveryCartItems(cartItemsToSessionItems(items));
    }
  }, [items]);

  const selectedShipOptionId = checkoutSession?.shipOptionId;

  const selectedShipOption = useMemo(
    () => deliveryOptions?.find((opt) => opt.id === selectedShipOptionId),
    [selectedShipOptionId, deliveryOptions]
  );

  const { mutate: updateShipOptionMutate } = useMutation({
    mutationFn: async (optionId: string) => {
      if (items.length > 0) {
        setRecoveryCartItems(cartItemsToSessionItems(items));
      }
      const resolvedSessionId = await checkoutCore.ensureSessionId(isTemp);
      return checkoutCore.updateShipOption(resolvedSessionId, optionId, isTemp);
    },
    onMutate: async (optionId) => {
      await queryClient.cancelQueries({ queryKey: ["TempCalculateTotal"] });

      const previousSession = queryClient.getQueryData<CheckoutSession>(checkoutSessionKey);
      if (previousSession?.items) {
        const updatedSession = {
          ...previousSession,
          shipOptionId: optionId,
        };
        queryClient.setQueryData<CheckoutSession>(checkoutSessionKey, updatedSession);
        syncCheckoutSessionCachesIfSameId(queryClient, updatedSession);
      }

      return { previousSession };
    },
    onSuccess: (data: CheckoutSuccessRes) => {
      queryClient.setQueryData(checkoutSessionKey, data);
      syncCheckoutSessionCachesIfSameId(queryClient, data);
    },
    onError: (err, optionId, context) => {
      void logClientError(err, { source: "useSelectShipOption", extra: { optionId } });
      if (context?.previousSession) {
        queryClient.setQueryData(checkoutSessionKey, context.previousSession);
        syncCheckoutSessionCachesIfSameId(queryClient, context.previousSession);
      }
      toast.error(t(msg.general.errSubmit1));
    },
  });

  const handleSelectShipOption = useCallback(
    (optionId: string) => {
      if (optionId === selectedShipOptionId) return;
      updateShipOptionMutate(optionId);
    },
    [selectedShipOptionId, updateShipOptionMutate]
  );

  return {
    selectedShipOption,
    handleSelectShipOption,
    isSessionReady,
  };
}
